import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";
import { Bell } from "lucide-react";

interface RealtimeNotification {
  id: string;
  type: "vote" | "comment" | "guest_joined" | "event_update";
  eventId: string;
  eventName: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

export const useRealtimeNotifications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<RealtimeNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user?.id) return;

    // Subscribe to event votes
    const votesChannel = supabase
      .channel('event-votes-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'event_votes'
        },
        async (payload) => {
          console.log('New vote received:', payload);
          
          // Fetch event details
          const { data: vote } = await supabase
            .from('event_votes')
            .select(`
              event_id,
              events!inner(name, user_id)
            `)
            .eq('id', payload.new.id)
            .single();

          if (vote?.events?.user_id === user.id) {
            const notification: RealtimeNotification = {
              id: payload.new.id,
              type: "vote",
              eventId: payload.new.event_id,
              eventName: vote.events.name,
              message: "Someone just voted on your event!",
              timestamp: new Date(),
              read: false
            };

            setNotifications(prev => [notification, ...prev]);
            setUnreadCount(prev => prev + 1);

            toast({
              title: "🗳️ New Vote!",
              description: `Someone voted on ${vote.events.name}`,
            });
          }
        }
      )
      .subscribe();

    // Subscribe to new guests
    const guestsChannel = supabase
      .channel('event-guests-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'event_guests'
        },
        async (payload) => {
          console.log('New guest added:', payload);

          const { data: guest } = await supabase
            .from('event_guests')
            .select(`
              event_id,
              name,
              events!inner(name, user_id)
            `)
            .eq('id', payload.new.id)
            .single();

          if (guest?.events?.user_id === user.id) {
            const notification: RealtimeNotification = {
              id: payload.new.id,
              type: "guest_joined",
              eventId: payload.new.event_id,
              eventName: guest.events.name,
              message: `${guest.name || 'A guest'} was added to your event`,
              timestamp: new Date(),
              read: false
            };

            setNotifications(prev => [notification, ...prev]);
            setUnreadCount(prev => prev + 1);

            toast({
              title: "👤 New Guest",
              description: notification.message,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(votesChannel);
      supabase.removeChannel(guestsChannel);
    };
  }, [user?.id, toast]);

  const markAsRead = (notificationId: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications
  };
};
